import { isPublicRelayUrl, normalizeRelayUrl, uniqueRelayUrls } from "@/utils/url"
import { KINDS, tagValues, type NostrEvent, type NostrFilter } from "../types"

export interface RelayCollection {
  id: string
  name: string
  relays: string[]
  /** Where the collection came from: the curated list URL or `discovery`. */
  source: "curated" | "discovery" | "recommended"
}

/** Normalises the curated `collections.json` document (untrusted input). */
export function normalizeRelayCollections(raw: unknown, filterOnion = true): RelayCollection[] {
  const list =
    Array.isArray(raw) ? raw : raw && typeof raw === "object" ? (raw as { collections?: unknown }).collections : undefined
  if (!Array.isArray(list)) return []

  const collections: RelayCollection[] = []
  for (const entry of list) {
    if (!entry || typeof entry !== "object") continue
    const record = entry as Record<string, unknown>
    const name = typeof record.name === "string" ? record.name.trim().slice(0, 80) : ""
    const id = typeof record.id === "string" && record.id.trim() ? record.id.trim() : name
    const relays = Array.isArray(record.relays) ? record.relays : []
    const cleanRelays = uniqueRelayUrls(
      relays.filter((relay): relay is string => typeof relay === "string").filter((relay) => {
        const normalized = normalizeRelayUrl(relay)
        return normalized ? isPublicRelayUrl(normalized, filterOnion) : false
      }),
    )
    if (!name || cleanRelays.length === 0) continue
    collections.push({ id, name, relays: cleanRelays, source: "curated" })
  }
  return collections
}

export async function fetchRelayCollections(
  url: string,
  options: { timeoutMs?: number; filterOnion?: boolean } = {},
): Promise<RelayCollection[]> {
  if (!url) return []
  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), options.timeoutMs ?? 7000)
  try {
    const response = await fetch(url, { signal: controller.signal, cache: "no-store" })
    if (!response.ok) throw new Error(`HTTP ${response.status}`)
    return normalizeRelayCollections(await response.json(), options.filterOnion ?? true)
  } finally {
    clearTimeout(timer)
  }
}

/** Language specific relay recommendations, mirroring Jumble's explore page. */
export function recommendedRelaysForLanguage(language: string): string[] {
  const lang = (language || "en").toLowerCase()
  if (lang.startsWith("zh")) {
    return ["wss://relay.nostrzh.org/", "wss://relay.nostr.moe/", "wss://lang.relays.land/zh", "wss://relay.stream/"]
  }
  if (lang.startsWith("ja")) return ["wss://yabu.me/", "wss://lang.relays.land/ja"]
  if (lang.startsWith("es")) return ["wss://lang.relays.land/es"]
  if (lang.startsWith("it")) return ["wss://lang.relays.land/it"]
  if (lang.startsWith("pt")) return ["wss://lang.relays.land/pt"]
  if (lang.startsWith("pl")) return ["wss://lang.relays.land/pl"]
  return []
}

/** NIP-66 relay discovery events (kind 30166) published by relay monitors. */
export function discoveryFilter(limit = 250): NostrFilter {
  return { kinds: [KINDS.RELAY_DISCOVERY], limit }
}

export function relayUrlsFromDiscoveryEvents(events: NostrEvent[], filterOnion = true): string[] {
  const urls: string[] = []
  for (const event of events) {
    if (event.kind !== KINDS.RELAY_DISCOVERY) continue
    const candidates = [...tagValues(event, "d"), ...tagValues(event, "r")]
    for (const candidate of candidates) {
      const normalized = normalizeRelayUrl(candidate)
      if (!normalized || !isPublicRelayUrl(normalized, filterOnion)) continue
      if (!urls.includes(normalized)) urls.push(normalized)
    }
  }
  return urls
}
